/**
 * Фоновый массовый замер скорости в очереди чекера (админка).
 * Живёт вне React: не обрывается при переходе на другой раздел / размонтировании страницы.
 * Состояние очереди — БД через writeAdminCheckerQueue (in-memory + debounce PUT); UI через subscribe.
 */
import { message } from 'antd';
import {
  ensureAdminCheckerQueueLoaded,
  flushAdminCheckerQueueNow,
  readAdminCheckerQueue,
  writeAdminCheckerQueue,
} from '../constants/adminCheckerQueue';
import { runAdminCheckerSpeedProbe } from './adminProxySpeedStream';

const ADMIN_SPEED_QUEUE_CONCURRENCY = 10;

function adminQueueRowKey(row) {
  if (row && row.external_key != null) {
    return `ext-${row.external_key}`;
  }
  return `id-${row.proxy_id}`;
}

function adminQueueBusyKey(row) {
  if (row && row.external_key != null) {
    return row.external_key;
  }
  return row.proxy_id;
}

async function mapPool(items, concurrency, iterator) {
  if (!items.length) return [];
  let index = 0;
  const results = new Array(items.length);
  async function worker() {
    while (true) {
      const i = index;
      index += 1;
      if (i >= items.length) break;
      results[i] = await iterator(items[i], i);
    }
  }
  const n = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

/**
 * Обновить строку очереди в кэше/БД (та же логика, что в ProxyChecker.applyAdminSpeedResultToRow).
 */
export function applyAdminSpeedResultToStoredQueue(rowKeyStr, result, speedCheckedAt, errMsg) {
  const prev = readAdminCheckerQueue();
  const next = prev.map((r) => {
    const rk = String(adminQueueRowKey(r));
    if (rk !== rowKeyStr) return r;
    if (errMsg) {
      return {
        ...r,
        speedMeasureError: errMsg,
      };
    }
    const dlAvg =
      result.downloadMbpsAvg != null && Number.isFinite(Number(result.downloadMbpsAvg))
        ? Number(result.downloadMbpsAvg)
        : null;
    const ulAvg =
      result.uploadMbpsAvg != null && Number.isFinite(Number(result.uploadMbpsAvg))
        ? Number(result.uploadMbpsAvg)
        : null;
    let dl = result.downloadMbpsMax;
    let ul = result.uploadMbpsMax;
    dl = dl != null && Number.isFinite(Number(dl)) ? Number(dl) : null;
    ul = ul != null && Number.isFinite(Number(ul)) ? Number(ul) : null;
    if (result.error) {
      return { ...r, speedMeasureError: String(result.error) };
    }
    return {
      ...r,
      speedMeasureError: undefined,
      downloadMbpsAvg: dlAvg,
      uploadMbpsAvg: ulAvg,
      downloadMbpsMax: dl,
      uploadMbpsMax: ul,
      downloadMbps: dl,
      uploadMbps: ul,
      speedCheckedAt,
      speed:
        dl != null || ul != null
          ? `${dl != null ? `${dl.toFixed(2)} ↓` : '—'} · ${ul != null ? `${ul.toFixed(2)} ↑` : '—'} Мб/с`
          : '—',
    };
  });
  writeAdminCheckerQueue(next);
}

let massSpeedRunning = false;
let massSpeedBusyIds = new Set();
let speedMeasureUiByQueueKey = {};
let massSpeedAbortRef = null;

const listeners = new Set();

function notify() {
  const snap = getAdminCheckerMassSpeedSnapshot();
  listeners.forEach((fn) => {
    try {
      fn(snap);
    } catch {
      // ignore subscriber errors
    }
  });
}

export function getAdminCheckerMassSpeedSnapshot() {
  return {
    massSpeedRunning,
    massSpeedBusyIds: new Set(massSpeedBusyIds),
    speedMeasureUiByQueueKey: { ...speedMeasureUiByQueueKey },
  };
}

/**
 * @param {(snap: ReturnType<typeof getAdminCheckerMassSpeedSnapshot>) => void} listener
 * @returns {() => void}
 */
export function subscribeAdminCheckerMassSpeed(listener) {
  listener(getAdminCheckerMassSpeedSnapshot());
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function updateSessionSpeedMeasureUi(queueKey, ui) {
  if (queueKey == null) return;
  const k = String(queueKey);
  speedMeasureUiByQueueKey = { ...speedMeasureUiByQueueKey, [k]: ui };
  notify();
}

function clearSessionSpeedMeasureUi(queueKey) {
  if (queueKey == null) return;
  const k = String(queueKey);
  if (!(k in speedMeasureUiByQueueKey)) return;
  const next = { ...speedMeasureUiByQueueKey };
  delete next[k];
  speedMeasureUiByQueueKey = next;
  notify();
}

/**
 * Массовый замер по выбранным ключам строк (как adminQueueRowKey).
 * Не привязан к жизненному циклу компонента ProxyChecker.
 * @param {Iterable<string>|string[]} selectedKeys
 */
export async function startAdminCheckerMassSpeed(selectedKeys) {
  const keys = [...selectedKeys];
  if (keys.length === 0) {
    message.warning('Отметьте прокси в таблице');
    return;
  }
  await ensureAdminCheckerQueueLoaded();
  const queue = readAdminCheckerQueue();
  const keySet = new Set(keys.map((k) => String(k)));
  const rows = queue.filter((r) => keySet.has(String(adminQueueRowKey(r))));
  if (rows.length === 0) {
    message.warning('Отметьте прокси в таблице');
    return;
  }

  if (massSpeedAbortRef) {
    massSpeedAbortRef.abort();
  }
  const ac = new AbortController();
  massSpeedAbortRef = ac;

  massSpeedRunning = true;
  speedMeasureUiByQueueKey = {};
  massSpeedBusyIds = new Set();
  rows.forEach((r) => {
    const bk = adminQueueBusyKey(r);
    if (bk != null) massSpeedBusyIds.add(bk);
  });
  notify();

  const speedCheckedAt = new Date().toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  try {
    await mapPool(rows, ADMIN_SPEED_QUEUE_CONCURRENCY, async (row) => {
      const rk = String(adminQueueRowKey(row));
      if (ac.signal.aborted) return;
      try {
        const result = await runAdminCheckerSpeedProbe(row, {
          signal: ac.signal,
          onProgress: (ui) => {
            updateSessionSpeedMeasureUi(rk, ui);
          },
        });
        applyAdminSpeedResultToStoredQueue(rk, result, speedCheckedAt, null);
      } catch (e) {
        if (ac.signal.aborted) return;
        const msg = e instanceof Error ? e.message : 'Ошибка замера';
        applyAdminSpeedResultToStoredQueue(rk, {}, speedCheckedAt, msg);
      } finally {
        clearSessionSpeedMeasureUi(rk);
        const bk = adminQueueBusyKey(row);
        if (bk != null) {
          const nb = new Set(massSpeedBusyIds);
          nb.delete(bk);
          massSpeedBusyIds = nb;
        }
        notify();
      }
    });
    if (!ac.signal.aborted) {
      message.success('Массовый замер скорости завершён');
    }
  } catch (e) {
    if (!ac.signal.aborted) {
      message.error(e instanceof Error ? e.message : 'Ошибка массового замера');
    }
  } finally {
    massSpeedRunning = false;
    massSpeedAbortRef = null;
    massSpeedBusyIds = new Set();
    speedMeasureUiByQueueKey = {};
    notify();
    void flushAdminCheckerQueueNow();
  }
}
